import React, { useEffect, useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { Board, MinimalBoard } from "shared/datamodel/schemas/board";
import { BatchedItemsGrid } from "./batched-items-grid";
import NoCanvasesEmptyStateNew from "./no-canvases-empty-state-new";
import { BlankCanvasTemplate, CanvasTemplate } from "shared/datamodel/schemas/canvas-template";
import style from "./boards-by-section.module.css";
import { navigateToBoard } from "./boards-grid-utils";
import classNames from "classnames";
import { useBoardsCache } from "frontend/hooks/caching/use-boards-cache";
import { BoardCardNew } from "./board-card-new";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { BoardSection, BoardSectionType, getSectionId } from "shared/datamodel/schemas/board-section";
import { SectionTitle } from "./section-title";
import SectionProjects from "./section-projects";
import { PlusBigIcon } from "frontend/icons/plus-big";

export default function BoardsBySection({
  section,
  onSelected,
  createNewBoard,
  onDuplicateError,
  setCanvasToMove,
  selectedTeamId,
  selectedProjectId,
  onEditSeats,
  onDeleteSelectedTeam,
  setSelectedTeam,
  onDeleteSelectedProject,
  setSelectedProject,
  isMovingBoards,
  setIsMovingBoards,
}: {
  section: BoardSection;
  onSelected: (documentId: string) => void;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onDuplicateError: (number: number) => void;
  setCanvasToMove: (board: Board | null) => void;
  selectedTeamId: string | null;
  selectedProjectId: string | null;
  onEditSeats: () => void;
  onDeleteSelectedTeam: () => void;
  setSelectedTeam: (teamId: string) => void;
  onDeleteSelectedProject: () => void;
  setSelectedProject: (projectId: string, urlId?: string) => void;
  isMovingBoards: boolean;
  setIsMovingBoards: (value: boolean) => void;
}) {
  const {
    isLoading: isLoadingBoards,
    getSection,
    fetchRecentBoards,
    fetchMyBoards,
    fetchSharedWithMeBoards,
    fetchBoardsByInstance,
  } = useBoardsCache();

  const sectionId = getSectionId(section.type, section.id);
  const currentBoards = useMemo(() => getSection(sectionId), [sectionId, getSection]);

  useEffect(() => {
    if (section.type === BoardSectionType.RECENT) {
      fetchRecentBoards();
    }
    if (section.type === BoardSectionType.MY_BOARDS) {
      fetchMyBoards();
    }
    if (section.type === BoardSectionType.SHARED_WITH_ME) {
      fetchSharedWithMeBoards();
    }
    if (section.type === BoardSectionType.WORKSPACE && section.id) {
      fetchBoardsByInstance({ instanceId: section.id, instanceType: InstanceType.Workspace });
    }
    if (section.type === BoardSectionType.PROJECT && section.id) {
      fetchBoardsByInstance({ instanceId: section.id, instanceType: InstanceType.Project });
    }
  }, [section.id, section.type, fetchRecentBoards, fetchMyBoards, fetchBoardsByInstance, fetchSharedWithMeBoards]);

  const renderCard = (board: MinimalBoard | null, index: number) => {
    if (board) {
      return (
        <BoardCardNew
          onClick={() => {
            onSelected(board.documentId);
            navigateToBoard(board);
          }}
          board={board}
          key={board.id}
          showDynamicPreview={false}
          boardIndex={index}
          onDuplicateError={onDuplicateError}
          setCanvasToMove={setCanvasToMove}
        />
      );
    }
  };

  const renderNewCanvasCard = () => {
    if ([BoardSectionType.MY_BOARDS, BoardSectionType.PROJECT, BoardSectionType.WORKSPACE].includes(section.type)) {
      return (
        <div
          className={style.addCanvasContainer}
          onClick={() =>
            createNewBoard({
              template: BlankCanvasTemplate,
              inputProjectId: selectedProjectId,
              inputTeamId: selectedTeamId,
              source: "section-boards",
            })
          }
          data-testid="add-canvas-card"
        >
          <div className={style.addCanvas}>
            <PlusBigIcon />
          </div>
          <div className={style.addCanvasText}>Add canvas</div>
        </div>
      );
    } else {
      return null;
    }
  };

  const renderBoardsEmptyState = () => {
    const emptyStateMessages: Record<Exclude<BoardSectionType, BoardSectionType.TEMPLATES>, string> = {
      [BoardSectionType.PROJECT]: "No canvases in this project yet!",
      [BoardSectionType.WORKSPACE]: "No canvases in this workspace yet!",
      [BoardSectionType.MY_BOARDS]: "No canvases yet!",
      [BoardSectionType.RECENT]: "No recent canvases yet!",
      [BoardSectionType.SHARED_WITH_ME]: "No canvases shared with you yet!",
    };

    if (section.type === BoardSectionType.TEMPLATES) return null;

    return (
      <NoCanvasesEmptyStateNew
        subtitle={emptyStateMessages[section.type]}
        createNewBoard={createNewBoard}
        selectedTeamId={selectedTeamId}
        selectedProjectId={selectedProjectId}
      />
    );
  };

  const renderContent = () => {
    if (isLoadingBoards || !currentBoards) {
      return (
        <>
          <Skeleton count={1} className={classNames(style.titleLine, style.titleLineSkeleton)} />
          <Skeleton count={20} className={style.skeletonCard} containerClassName={style.skeletonCardContainer} />
        </>
      );
    }

    return (
      <div className={style.section}>
        <SectionTitle
          section={section}
          onEditSeats={onEditSeats}
          createNewBoard={createNewBoard}
          onDeleteSelectedTeam={onDeleteSelectedTeam}
          setSelectedTeam={setSelectedTeam}
          onDeleteSelectedProject={onDeleteSelectedProject}
        />
        <SectionProjects
          section={section}
          createNewBoard={createNewBoard}
          onEditSeats={onEditSeats}
          setSelectedProject={setSelectedProject}
          setIsMovingBoards={setIsMovingBoards}
        />
        {currentBoards?.length === 0 && !isLoadingBoards && !isMovingBoards ? (
          renderBoardsEmptyState()
        ) : (
          <BatchedItemsGrid
            items={currentBoards}
            gridContainerClass={style.boardsGrid}
            cellRender={renderCard}
            batchSize={50}
            newCanvasCard={renderNewCanvasCard()}
          />
        )}
      </div>
    );
  };

  return <div className={style.boardsContainer}>{renderContent()}</div>;
}
